import React, { useState } from 'react';
import { Alert, Button, DatePicker, Form, Input, InputNumber, Modal, Radio, Select, Space, Table, Tag } from 'antd';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { checkPermission } from '@/utils/permission';
import { paymentMethodOptions, renderMoney } from '@/utils/helpers';
import {
    buildAutomaticDepositAllocations,
    buildDepositAllocationPayload,
    getDepositAllocationError,
} from './customerDepositAllocation';
import { transferCodeText, transferHistoryText } from './customerFundTransferPresentation';
import {
    adjustCustomerDeposit,
    applyCustomerDeposit,
    receiveCustomerDeposit,
    refundCustomerDeposit,
    transferCustomerDeposit,
} from '@/redux/actions/receiptActions';

const transactionLabels = {
    DEPOSIT_RECEIVED: 'Nhận tiền cọc',
    DEPOSIT_APPLIED_TO_SERVICE: 'Sử dụng cọc cho dịch vụ',
    DEPOSIT_TARGET_CHANGED: 'Đổi dịch vụ dự kiến',
    DEPOSIT_ADJUSTMENT_IN: 'Điều chỉnh tăng',
    DEPOSIT_ADJUSTMENT_OUT: 'Điều chỉnh giảm',
    DEPOSIT_REFUNDED: 'Trả cọc cho khách',
    DEPOSIT_REVERSAL: 'Đảo giao dịch cọc',
    DEPOSIT_TRANSFERRED_OUT: 'Chuyển cọc cho khách hàng khác',
    DEPOSIT_TRANSFERRED_IN: 'Nhận cọc từ khách hàng khác',
};
const statusLabels = {
    ACTIVE: 'Còn nguyên', PARTIALLY_USED: 'Đã sử dụng một phần', USED: 'Đã sử dụng hết',
    REFUNDED: 'Đã trả hết', CANCELLED: 'Đã điều chỉnh hết',
};
const makeKey = prefix => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const text = (value, fallback = '—') => value == null || value === '' ? fallback : String(value);
const moneyInputProps = {
    formatter: value => `${value == null ? '' : value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    parser: value => (value || '').replace(/,/g, ''),
};

/** Nhận, sử dụng, chuyển, điều chỉnh, trả và tra cứu tiền cọc của khách hàng. */
export default function CustomerDepositActions({
    customerId,
    data,
    reload,
    autoOpenReceive = false,
    showActions = true,
    onClose,
    renderActions,
}) {
    const dispatch = useDispatch();
    const branches = useSelector(state => state.config.branches || []);
    const [form] = Form.useForm();
    const [mode, setMode] = useState(autoOpenReceive ? 'receive' : null);
    const [selected, setSelected] = useState(null);
    const [selectedServiceIds, setSelectedServiceIds] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const canReceive = checkPermission('customer_deposit', 'receive');
    const canApply = checkPermission('customer_deposit', 'apply');
    const canTransfer = checkPermission('customer_deposit', 'transfer');
    const canAdjust = checkPermission('customer_deposit', 'adjust');
    const canRefund = checkPermission('customer_deposit', 'refund');
    const canView = checkPermission('customer_deposit', 'view');
    const deposits = data.active_deposits || [];
    const services = (data.services || []).filter(item => Number(item.outstanding_amount) > 0);
    const serviceOptions = services.map(item => ({
        value: item.id,
        label: `${text(item.name, `Dịch vụ #${item.id}`)} · còn nợ ${renderMoney(item.outstanding_amount)}`,
    }));

    const open = (nextMode, deposit = null) => {
        setSelected(deposit);
        setSelectedServiceIds([]);
        setMode(nextMode);
        form.resetFields();
        form.setFieldsValue({
            effective_at: moment(),
            payment_method: null,
            branch_id: data.customer_branch_id || null,
            target_customer_service_ids: deposit?.target_customer_service_ids ||
                (deposit?.target_customer_service_id ? [deposit.target_customer_service_id] : []),
            amount: nextMode === 'refund' ? Number(deposit?.current_balance || 0) : null,
            idempotency_key: makeKey(`deposit-${nextMode}`),
        });
    };

    const close = () => {
        setMode(null);
        setSelected(null);
        setSelectedServiceIds([]);
        form.resetFields();
        if (autoOpenReceive && onClose) onClose();
    };
    const command = values => {
        const common = {
            deposit_id: selected?.id,
            reason: values.reason,
            idempotency_key: values.idempotency_key,
        };
        if (mode === 'receive') return dispatch(receiveCustomerDeposit({
            customer_id: customerId,
            amount: values.amount,
            received_at: values.effective_at.format('YYYY-MM-DD HH:mm:ss'),
            payment_method: values.payment_method,
            branch_id: values.branch_id,
            payment_breakdowns: [{ payment_method: values.payment_method, amount: values.amount }],
            target_customer_service_ids: values.target_customer_service_ids || [],
            reason: values.reason,
            idempotency_key: values.idempotency_key,
        }));
        if (mode === 'apply') return dispatch(applyCustomerDeposit({
            ...common, allocations: buildDepositAllocationPayload(
                values.customer_service_ids, values.allocation_amounts
            ),
            applied_at: values.effective_at.format('YYYY-MM-DD HH:mm:ss'),
        }));
        if (mode === 'transfer') return dispatch(transferCustomerDeposit({
            ...common, target_customer_service_ids: values.target_customer_service_ids || [],
            effective_at: values.effective_at.format('YYYY-MM-DD HH:mm:ss'),
        }));
        if (mode === 'adjust') return dispatch(adjustCustomerDeposit({
            ...common, delta_amount: values.delta_amount,
            effective_at: values.effective_at.format('YYYY-MM-DD HH:mm:ss'),
        }));
        return dispatch(refundCustomerDeposit({
            ...common, amount: values.amount, payment_method: values.payment_method,
            refund_at: values.effective_at.format('YYYY-MM-DD HH:mm:ss'),
        }));
    };

    const submit = () => form.validateFields().then(values => {
        setSubmitting(true);
        return command(values).then(async () => { await reload(); close(); }).finally(() => setSubmitting(false));
    });

    const title = {
        receive: 'Nhận tiền khách đặt cọc', apply: 'Sử dụng tiền cọc cho dịch vụ',
        transfer: 'Chuyển dịch vụ dự kiến', adjust: 'Điều chỉnh khoản đặt cọc', refund: 'Trả tiền cọc cho khách',
    }[mode];
    const columns = [
        { title: 'Phiếu thu', dataIndex: 'receipt_code', render: value => text(value) },
        { title: 'Ngày nhận', dataIndex: 'received_at', render: value => value ? moment(value).format('DD/MM/YYYY HH:mm') : '—' },
        { title: 'Dịch vụ dự kiến', dataIndex: 'target_service_names', render: values => values?.length ? values.join(', ') : 'Chưa xác định' },
        { title: 'Số dư cọc', dataIndex: 'current_balance', align: 'right', render: renderMoney },
        { title: 'Trạng thái', dataIndex: 'status', render: value => <Tag>{statusLabels[value] || 'Chưa xác định'}</Tag> },
        {
            title: 'Thao tác', key: 'actions', render: (_, row) => <Space wrap>
                {canApply && <Button size="small" onClick={() => open('apply', row)}>Sử dụng</Button>}
                {canTransfer && <Button size="small" onClick={() => open('transfer', row)}>Chuyển</Button>}
                {canAdjust && <Button size="small" onClick={() => open('adjust', row)}>Điều chỉnh</Button>}
                {canRefund && <Button size="small" danger onClick={() => open('refund', row)}>Trả cọc</Button>}
            </Space>,
        },
    ];

    return <>
        {showActions && (renderActions ? renderActions({
            openReceive: () => open('receive'),
            openDepositList: () => setMode('list'),
            openDepositHistory: () => setMode('history'),
            canReceive,
            canView,
        }) : <Space wrap>
            {canReceive && <Button type="primary" onClick={() => open('receive')}>Nhận tiền cọc</Button>}
            {canView && <Button onClick={() => setMode('list')}>Quản lý tiền cọc</Button>}
            {canView && <Button onClick={() => setMode('history')}>Lịch sử tiền cọc</Button>}
        </Space>)}
        <Modal visible={mode === 'list'} width={isMobile ? '100%' : '90%'} title="Các khoản cọc còn hiệu lực"
            footer={<Button type="primary" onClick={close}>Đóng</Button>} onCancel={close} maskClosable={false}>
            <Alert type="info" message="Số dư cọc là tiền khách đã nộp nhưng chưa dùng hết cho dịch vụ. Chỉ các khoản còn số dư mới có thể sử dụng, chuyển, điều chỉnh hoặc trả lại." style={{ marginBottom: 12 }} />
            <Table rowKey="id" size="small" dataSource={deposits} columns={columns} scroll={{ x: 900 }} pagination={{ pageSize: 10 }} />
        </Modal>
        <Modal visible={mode === 'history'} width={isMobile ? '100%' : '90%'} title="Lịch sử tiền khách đặt cọc"
            footer={<Button type="primary" onClick={close}>Đóng</Button>} onCancel={close} maskClosable={false}>
            <Alert type="info" message="Mỗi dòng cho biết số dư cọc trước và sau giao dịch. Các giao dịch cũ được giữ nguyên để phục vụ đối chiếu." style={{ marginBottom: 12 }} />
            <Table rowKey="id" size="small" dataSource={data.deposit_history || []}
                scroll={{ x: "max-content" }}
                pagination={{ pageSize: 10 }}
                columns={[
                    { title: 'Thời gian', dataIndex: 'effective_at', width: 155, render: value => value ? moment(value).format('DD/MM/YYYY HH:mm') : '—' },
                    { title: 'Loại giao dịch', dataIndex: 'transaction_type', width: 210, render: value => <Tag>{transactionLabels[value] || 'Giao dịch tiền cọc'}</Tag> },
                    { title: 'Giao dịch đối ứng', width: 260, render: (_, row) => transferHistoryText(row) || '—' },
                    { title: 'Loại nguồn', width: 180, render: (_, row) => row.transfer_code ? 'Tiền cọc' : '—' },
                    { title: 'Lần chuyển', width: 145, render: (_, row) => transferCodeText(row) },
                    { title: 'Phiếu thu', dataIndex: 'receipt_code', width: 105, render: (value, row) => row.transfer_code ? '—' : text(value) },
                    { title: 'Dịch vụ', dataIndex: 'service_name', width: 230, render: value => text(value) },
                    { title: 'Số tiền', dataIndex: 'amount', align: 'right', width: 130, render: (value, row) => `${row.direction === 'DEBIT' ? '-' : row.direction === 'CREDIT' ? '+' : ''}${renderMoney(value)}` },
                    { title: 'Trước', dataIndex: 'balance_before', align: 'right', width: 130, render: renderMoney },
                    { title: 'Sau', dataIndex: 'balance_after', align: 'right', width: 130, render: renderMoney },
                    { title: 'Người thao tác', dataIndex: 'actor_name', width: 250, render: value => text(value, 'Hệ thống') },
                    { title: 'Nội dung', dataIndex: 'reason', width: 260, render: value => text(value) },
                ]} />
        </Modal>
        <Modal visible={!!title} destroyOnClose forceRender width={isMobile ? '100%' : '50%'} title={title}
            okText="Xác nhận" cancelText="Hủy" confirmLoading={submitting} onOk={submit} onCancel={close} maskClosable={false}>
            <Form form={form} layout="vertical" initialValues={autoOpenReceive ? {
                effective_at: moment(),
                payment_method: null,
                branch_id: data.customer_branch_id || null,
                target_customer_service_ids: [],
                amount: null,
                idempotency_key: makeKey('deposit-receive'),
            } : undefined}>
                <Alert type={mode === 'refund' || mode === 'adjust' ? 'warning' : 'info'} message={{
                    receive: 'Ghi nhận tiền khách thực tế đã nộp vào quỹ cọc; khoản này chưa tự thanh toán công nợ dịch vụ.',
                    apply: 'Số tiền được chọn sẽ giảm đồng thời số dư cọc và công nợ của các dịch vụ nhận tiền.',
                    transfer: 'Chỉ thay đổi dịch vụ dự kiến của khoản cọc; số dư và chủ sở hữu khoản tiền không thay đổi.',
                    adjust: 'Điều chỉnh tăng hoặc giảm số dư cọc phải có căn cứ; lịch sử giao dịch gốc vẫn được giữ lại.',
                    refund: 'Ghi nhận trả tiền cọc cho khách và làm giảm số dư cọc; không dùng thao tác này để chuyển tiền sang khách hàng khác.',
                }[mode]} style={{ marginBottom: 16 }} />
                {(mode === 'receive' || mode === 'refund') && <Form.Item name="amount" label="Số tiền" extra={mode === 'refund' ? 'Không được vượt số dư còn lại của khoản cọc.' : 'Đây là số tiền khách thực tế nộp trong lần này.'} rules={[
                    { required: true, message: 'Vui lòng nhập số tiền.' },
                    { validator: (_, value) => Number(value) > 0 && (!selected || Number(value) <= Number(selected.current_balance)) ? Promise.resolve() : Promise.reject(new Error('Số tiền không hợp lệ hoặc vượt số dư cọc.')) },
                ]}><InputNumber {...moneyInputProps} min={1} precision={0} style={{ width: '100%' }} /></Form.Item>}
                {mode === 'adjust' && <Form.Item name="delta_amount" label="Số tiền điều chỉnh (+ tăng, - giảm)" extra="Nhập số dương để bổ sung, số âm để giảm; kết quả không được làm số dư cọc âm." rules={[
                    { required: true, message: 'Vui lòng nhập số tiền điều chỉnh.' },
                    { validator: (_, value) => Number(value) !== 0 && Number(value) >= -Number(selected?.current_balance || 0) ? Promise.resolve() : Promise.reject(new Error('Điều chỉnh không được bằng 0 hoặc làm âm số dư cọc.')) },
                ]}><InputNumber {...moneyInputProps} precision={0} style={{ width: '100%' }} /></Form.Item>}
                {mode === 'apply' && <>
                    <Form.Item name="customer_service_ids" label="Dịch vụ nhận tiền cọc" extra="Số tiền phân bổ sẽ giảm công nợ của từng dịch vụ theo giá trị nhập bên dưới."
                        rules={[{ required: true, type: 'array', min: 1, message: 'Vui lòng chọn ít nhất một dịch vụ.' }]}>
                        <Select mode="multiple" showSearch optionFilterProp="label" options={serviceOptions}
                            onChange={values => {
                                const nextServiceIds = values || [];
                                setSelectedServiceIds(nextServiceIds);
                                form.setFieldsValue({
                                    allocation_amounts: buildAutomaticDepositAllocations(
                                        nextServiceIds, services, selected?.current_balance
                                    ),
                                });
                            }} />
                    </Form.Item>
                    {selectedServiceIds.map(customerServiceId => {
                        const option = serviceOptions.find(item => Number(item.value) === Number(customerServiceId));
                        const service = services.find(item => Number(item.id) === Number(customerServiceId));
                        return <Form.Item key={customerServiceId} name={['allocation_amounts', customerServiceId]}
                            label={`Số tiền phân bổ · ${option?.label || `Dịch vụ #${customerServiceId}`}`}
                            rules={[
                                { required: true, message: 'Vui lòng nhập số tiền phân bổ.' },
                                {
                                    validator: (_, value) => {
                                        const amounts = form.getFieldValue('allocation_amounts') || {};
                                        const error = getDepositAllocationError({
                                            value,
                                            outstandingAmount: service?.outstanding_amount,
                                            allocationAmounts: amounts, customerServiceIds: selectedServiceIds,
                                            customerServiceId, availableBalance: selected?.current_balance
                                        });
                                        return error ? Promise.reject(new Error(error)) : Promise.resolve();
                                    }
                                },
                            ]}>
                            <InputNumber {...moneyInputProps} min={1} precision={0} style={{ width: '100%' }} />
                        </Form.Item>;
                    })}
                    <Form.Item shouldUpdate noStyle>
                        {() => {
                            const amounts = form.getFieldValue('allocation_amounts') || {};
                            const total = selectedServiceIds.reduce((sum, id) => sum + Number(amounts[id] || 0), 0);
                            return <div style={{ marginBottom: 16 }}>
                                Tổng phân bổ: <strong>{renderMoney(total)}</strong> / Số dư cọc: <strong>{renderMoney(selected?.current_balance || 0)}</strong>
                            </div>;
                        }}
                    </Form.Item>
                </>}
                {(mode === 'receive' || mode === 'transfer') && <Form.Item name="target_customer_service_ids" label="Dịch vụ dự kiến" extra="Có thể chọn nhiều dịch vụ hoặc để trống khi khách chưa chốt.">
                    <Select mode="multiple" allowClear showSearch optionFilterProp="label" options={serviceOptions} />
                </Form.Item>}
                {mode === 'receive' && <Form.Item name="payment_method" label="Hình thức thu" rules={[{ required: true, message: 'Vui lòng chọn hình thức.' }]}>
                    <Radio.Group options={(paymentMethodOptions || []).map(item => ({ value: item.value, label: item.label }))} />
                </Form.Item>}
                {mode === 'refund' && <Form.Item name="payment_method" label="Hình thức hoàn" rules={[{ required: true, message: 'Vui lòng chọn hình thức.' }]}>
                    <Radio.Group options={(paymentMethodOptions || []).map(item => ({ value: item.value, label: item.label }))} />
                </Form.Item>}
                {mode === 'receive' && <Form.Item name="branch_id" label="Cơ sở thực tế thu tiền" rules={[{ required: true, message: 'Vui lòng chọn cơ sở thực tế thu tiền.' }]}>
                    <Select showSearch optionFilterProp="label" options={branches.map(branch => ({ value: branch.id, label: branch.name }))} />
                </Form.Item>}
                <Form.Item name="effective_at" label="Thời gian giao dịch" extra="Chọn thời điểm nghiệp vụ thực tế để lịch sử và báo cáo ghi nhận đúng kỳ." rules={[{ required: true, message: 'Vui lòng chọn thời gian.' }]}>
                    <DatePicker showTime style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="reason" label="Ghi chú" rules={[{ required: false, whitespace: true, message: 'Vui lòng nhập nội dung nghiệp vụ.' }]}>
                    <Input.TextArea rows={3} maxLength={1000} />
                </Form.Item>
                <Form.Item name="idempotency_key" hidden><Input /></Form.Item>
            </Form>
        </Modal>
    </>;
}
